// The TA Manager's assignment queue — `#requisitions/assignment`, and `#requisitions/queue` re-pointed here so old links
// still land (DS §4 B2 row; G §16 "Assign/reassign Recruiter: … TA Mgr Approve"). "TA Manager assigns based on workload
// and expertise." Each approved request or created requisition still without a Recruiter is an Assignment card (row 86)
// with the recommended Recruiter, the workload (live searches owned) and expertise (same level, department, job family)
// as numbers — no score decides — and *Assign* (`recruiter.assign`, the record's row_version) or *Reassign* to another
// Recruiter from the same ranked list (its hotspot keeps the key `assign.row.<n>.choose` the frames name). Reassignment
// requests ("Recruiter/HRBP may request reassignment; TA Manager approves.") carry the requester, the reason and
// *Approve* (to the proposed Recruiter, or the one chosen) · *Return* (`recruiter.decide_reassignment`). No Claim exists
// on any seat ("No recruiter self-claim.").
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useMemo, useState } from "react";
import {
  recruiterAssign, recruiterDecideReassignment, recruiterQueue, recruiterRecommendation, type AwaitingRow, type RankedRecruiter, type ReassignmentRow,
} from "@/api/tools";
import { isClientRefusal, useWrite } from "@/api/useTool";
import { useShellCrumbs } from "@/shell/crumbs";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Assignment, Banner, Button, COMMON_LABELS, Card, EmptyState, ErrorStrip, Field, Flex, G6_CAPABILITY_LABELS, G6_RULE_LABELS,
  PageHeader, Paragraph, Person, PersonPick, RECRUITER_ASSIGNMENT_STATUS, ROLE_LABELS, RULE_LABELS, Skeleton, Stack, Stamp, SURFACE_LABELS, Tabs,
  TextLink, LEVEL_DISPLAY, CORE_LABELS, type Level, type PersonOption,
} from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import { ASSIGN_LABELS, REQ_LABELS } from "../requisition/labels";

const READS = [["recruiter-queue"], ["requisitions"], ["requisition"], ["today"]] as const;

export function AssignmentQueue() {
  const { register } = useViewState();
  useEffect(() => register({ view: "requisitions-assignment", ids: {}, summary: "assignment queue" }), [register]);
  return (
    <QueueBody />
  );
}

/** The top bar reads *Requisitions › Assign/reassign Recruiter* (F-147, DC-232): the Requisitions crumb leads back too. */
const CRUMBS = [{ label: G6_CAPABILITY_LABELS.assignRecruiter }] as const;

function QueueBody() {
  useShellCrumbs(CRUMBS);
  const q = useSettledRead(["recruiter-queue"], () => recruiterQueue({}));
  const [tab, setTab] = useState("awaiting");
  if (q.error?.status === 404) return <NotFound route="requisitions/assignment" />;
  const awaiting = q.data?.awaiting ?? [];
  const reassignments = q.data?.reassignments ?? [];
  return (
    <Stack gap={16}>
      <TextLink href="#requisitions" icon="back" >{ASSIGN_LABELS.goBackToRequisitions}</TextLink>
      <PageHeader title={G6_CAPABILITY_LABELS.assignRecruiter} />
      <Paragraph>{G6_RULE_LABELS.taManagerAssigns}</Paragraph>
      {q.error && isClientRefusal(q.error) ? <Banner tone="red" live>{q.error.remedy || q.error.message}</Banner> : null}
      {q.error && !isClientRefusal(q.error) ? (
        <ErrorStrip
          message={q.error.remedy || RULE_LABELS.failedSyncRetryable}
          problem={{ status: q.error.status, refusal_code: q.error.code, detail: q.error.message, request_id: q.error.requestId }}
          onRetry={() => void q.refetch()}
          
        />
      ) : null}
      {q.isPending ? <Skeleton lines={6} /> : null}
      {q.data ? (
        <>
          <Tabs
            label={G6_CAPABILITY_LABELS.assignRecruiter}
            active={tab}
            onSelect={setTab}
            items={[
              { key: "awaiting", label: RECRUITER_ASSIGNMENT_STATUS.pendingTaManager, count: q.data.awaiting_total },
              { key: "reassignments", label: REQ_LABELS.reassignmentRequest, count: q.data.reassignment_total },
            ]}
          />
          {tab === "awaiting"
            ? (awaiting.length ? awaiting.map((row, index) => <AwaitingCard key={row.request_id} row={row} index={index} />) : <EmptyState>{CORE_LABELS.nothingNeedsAction}</EmptyState>)
            : (reassignments.length ? reassignments.map((row, index) => <ReassignmentCard key={row.reassignment_request_id} row={row} index={index} />) : <EmptyState>{CORE_LABELS.nothingNeedsAction}</EmptyState>)}
        </>
      ) : null}
    </Stack>
  );
}

/** Expertise: the Recruiter's past searches at the same level, in the same department and the same job family. */
const expertiseOf = (r: RankedRecruiter) => r.same_level + r.same_department + r.same_job_family;

/** Every Recruiter offered, with the figures the TA Manager assigns on (F-52): the live searches owned and the expertise,
 * beside each name as the recommended one carries them. */
export function recruiterOptions(ranked: readonly RankedRecruiter[], hs: (r: RankedRecruiter) => string | undefined): PersonOption[] {
  return ranked.map((r) => ({
    value: r.person_id,
    name: r.name ?? r.person_id,
    seat: `${ROLE_LABELS.recruiter} · ${SURFACE_LABELS.workload} ${r.open_owned} · ${SURFACE_LABELS.expertise} ${expertiseOf(r)}`,
    hs: hs(r),
  }));
}

/** Bars are relative to the busiest and the most expert in the list; the numbers themselves are shown beside them. */
function scale(ranked: readonly RankedRecruiter[]) {
  const expertise = expertiseOf;
  const maxLoad = Math.max(1, ...ranked.map((r) => r.open_owned));
  const maxExp = Math.max(1, ...ranked.map(expertise));
  return (r: RankedRecruiter) => ({ workload: (r.open_owned / maxLoad) * 100, expertise: (expertise(r) / maxExp) * 100, exp: expertise(r) });
}

function recordLink(row: { request_id: string; requisition_id?: string | null; title: string }, hs: string) {
  return row.requisition_id
    ? <TextLink href={`#requisitions/${row.requisition_id}`} hs={hs}>{`${row.requisition_id} · ${row.title}`}</TextLink>
    : <TextLink href={`#requests/${row.request_id}`} hs={hs}>{`${row.request_id} · ${row.title}`}</TextLink>;
}

function AwaitingCard({ row, index }: { row: AwaitingRow; index: number }) {
  const ranked = row.recommended;
  const [chosen, setChosen] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const assign = useWrite(
    (input: { request_id?: string; requisition_id?: string; recruiter_id: string; expected_version: number }, key) => recruiterAssign(input, key),
    READS,
  );
  const pick = ranked.find((r) => r.person_id === chosen) ?? ranked[0];
  const of = scale(ranked);
  const level = row.level && row.level in LEVEL_DISPLAY ? LEVEL_DISPLAY[row.level as Level] : null;
  return (
    <Stack gap={8}>
      <Flex gap={8} wrap>
        {recordLink(row, `assign.row.${index}.open`)}
        {level ? <Paragraph sm>{level}</Paragraph> : null}
        {row.department ? <Paragraph sm>{row.department}</Paragraph> : null}
      </Flex>
      {pick ? (
        <Assignment
          person={{ name: pick.name ?? pick.person_id, hueKey: pick.person_id, sub: chosen ? undefined : RECRUITER_ASSIGNMENT_STATUS.recommended }}
          workload={of(pick).workload}
          workloadValue={String(pick.open_owned)}
          expertise={of(pick).expertise}
          expertiseValue={String(of(pick).exp)}
          status="pendingTaManager"
          assign={{
            onClick: () => void assign.mutateAsync({ ...(row.requisition_id ? { requisition_id: row.requisition_id } : { request_id: row.request_id }), recruiter_id: pick.person_id, expected_version: row.row_version }).catch(() => undefined),
            busy: assign.isPending,
            hs: `assign.row.${index}.assign`,
          }}
          chooseAnother={ranked.length > 1 ? { onClick: () => setChoosing((open) => !open), hs: `assign.row.${index}.choose` } : undefined}
          refused={assign.error ? { reason: assign.error.remedy || assign.error.message } : undefined}
        />
      ) : <Banner tone="warn">{G6_RULE_LABELS.taManagerAssigns}</Banner>}
      {choosing ? (
        <PersonPick
          label={ROLE_LABELS.recruiter}
          options={recruiterOptions(ranked, (r) => `assign.row.${index}.pick.${r.rank}`)}
          value={pick?.person_id}
          onChange={(value) => { setChosen(value); setChoosing(false); }}
          defaultOpen
          
        />
      ) : null}
    </Stack>
  );
}

function ReassignmentCard({ row, index }: { row: ReassignmentRow; index: number }) {
  const [chosen, setChosen] = useState<string | null>(row.proposed_recruiter_id ?? null);
  const options = useSettledRead(["recruiter-recommendation", row.request_id], () => recruiterRecommendation({ request_id: row.request_id }));
  const decide = useWrite(
    (input: { reassignment_request_id: string; expected_version: number; decision: "approve" | "reject"; to_recruiter_id?: string | null }, key) => recruiterDecideReassignment(input, key),
    READS,
  );
  const ranked = useMemo(() => (options.data?.ranked ?? []).filter((r) => r.person_id !== row.from_recruiter_id), [options.data, row.from_recruiter_id]);
  // C1 (F-149): no control sends an empty Recruiter — Approve waits for one picked (the proposal preselected), and a
  // refusal naming the Recruiter is drawn on its field
  const act = (decision: "approve" | "reject") => void decide.mutateAsync({
    reassignment_request_id: row.reassignment_request_id, expected_version: row.row_version, decision, to_recruiter_id: decision === "approve" ? chosen || null : null,
  }).catch(() => undefined);
  const recruiterError = decide.error?.fieldErrors.to_recruiter_id ? COMMON_LABELS.required : undefined;
  return (
    <Card
      title={REQ_LABELS.reassignmentRequest}
      sub={recordLink(row, `assign.re.${index}.open`)}
      refused={decide.error && !recruiterError ? { reason: decide.error.remedy || decide.error.message } : undefined}
      
    >
      <Stack gap={12}>
        <Person name={row.requested_by_name ?? row.requested_by_id} hueKey={row.requested_by_id} sub={<Stamp actor={row.requested_by_name ?? row.requested_by_id} at={row.requested_at} />} />
        <Paragraph strong>{row.reason}</Paragraph>
        <Person name={row.from_recruiter_name ?? row.from_recruiter_id} hueKey={row.from_recruiter_id} sub={ROLE_LABELS.recruiter} />
        <Field label={ROLE_LABELS.recruiter} required error={recruiterError}>
          <PersonPick
            loading={options.isPending}
            options={recruiterOptions(ranked, (r) => `assign.re.${index}.pick.${r.rank}`)}
            value={chosen ?? undefined}
            onChange={(value) => setChosen(value || null)}
            
          />
        </Field>
        <Flex gap={8}>
          <Button
            tone="primary"
            onClick={() => act("approve")}
            busy={decide.isPending}
            blocked={chosen ? undefined : { reason: `${ROLE_LABELS.recruiter} · ${COMMON_LABELS.required}` }}
            
          >
            {ACTION_LABELS.approve}
          </Button>
          <Button tone="ghost" onClick={() => act("reject")} busy={decide.isPending} >{ACTION_LABELS.return}</Button>
        </Flex>
      </Stack>
    </Card>
  );
}
